import type {
  RegistrationData,
  TrackType,
  HealthConditionOption,
  CKMConditionOption,
  BehavioralHealthConditionOption,
} from '@/types/registration';

export type AlignmentCondition = {
  code: string;
  display: string;
  onsetDateTime: string;
};

export const ECKM_CONDITION_MAP: Record<HealthConditionOption, Omit<AlignmentCondition, 'onsetDateTime'>> = {
  highBloodPressure: { code: 'I10', display: 'Essential (primary) hypertension' },
  highCholesterol: { code: 'E78.5', display: 'Hyperlipidemia, unspecified' },
  prediabetes: { code: 'R73.09', display: 'Other abnormal glucose' },
  overweightOrObesity: { code: 'E66.9', display: 'Obesity, unspecified' },
};

export const CKM_CONDITION_MAP: Record<CKMConditionOption, Omit<AlignmentCondition, 'onsetDateTime'>> = {
  diabetes: { code: 'E11.9', display: 'Type 2 diabetes mellitus without complications' },
  chronicKidneyDisease: { code: 'N18.9', display: 'Chronic kidney disease, unspecified' },
  heartDisease: { code: 'I25.10', display: 'Atherosclerotic heart disease of native coronary artery without angina pectoris' },
};

export const BH_CONDITION_MAP: Record<BehavioralHealthConditionOption, Omit<AlignmentCondition, 'onsetDateTime'>> = {
  depression: { code: 'F32.9', display: 'Major depressive disorder, single episode, unspecified' },
  anxiety: { code: 'F41.9', display: 'Anxiety disorder, unspecified' },
};

const TRACK_FALLBACK: Record<TrackType, Omit<AlignmentCondition, 'onsetDateTime'>> = {
  eCKM: { code: 'I10', display: 'Essential (primary) hypertension' },
  CKM: { code: 'E11.9', display: 'Type 2 diabetes mellitus without complications' },
  BH: { code: 'F32.9', display: 'Major depressive disorder, single episode, unspecified' },
};

export function buildAlignmentConditions(
  registrationData: RegistrationData,
  track: TrackType,
): AlignmentCondition[] {
  const today = new Date().toISOString().split('T')[0];
  const conditions: AlignmentCondition[] = [];

  if (track === 'eCKM') {
    registrationData.eCKMConditions.selectedConditions.forEach((key) => {
      const entry = ECKM_CONDITION_MAP[key];
      if (entry) conditions.push({ ...entry, onsetDateTime: today });
    });
  } else if (track === 'CKM') {
    registrationData.ckmConditions.selectedConditions.forEach((key) => {
      const entry = CKM_CONDITION_MAP[key];
      if (entry) conditions.push({ ...entry, onsetDateTime: today });
    });
  } else if (track === 'BH') {
    registrationData.behavioralHealthConditions.selectedConditions.forEach((key) => {
      const entry = BH_CONDITION_MAP[key];
      if (entry) conditions.push({ ...entry, onsetDateTime: today });
    });
  }

  if (conditions.length === 0) {
    conditions.push({ ...TRACK_FALLBACK[track], onsetDateTime: today });
  }

  return conditions;
}
